import { z } from 'zod/v4';

/**
 * Sort columns the scoped users list endpoint accepts. Each key maps to a SQL
 * expression in `core/db/users/list-scoped.ts` (`SORT_COLUMNS`) — keep the two
 * in sync when adding/removing columns. Mirrors `SORT_COLUMNS_ENUM` from
 * `backend/src/2025-06/api-service/routes/schemas/v3/request/user.js`.
 */
export const UsersListSortColumns = [
  'firstName',
  'lastName',
  'company',
  'primaryCompany',
  'role',
  'updatedAt',
  'createdAt',
  'email',
  'isBlocked',
  'hasAgreedWithTerms',
  'hasAgreedWithTermsAt',
  'lastLoginAt',
] as const;

export type UsersListSortColumn = (typeof UsersListSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

// `undefined` must stay `undefined` (not coerce to `false`) — `selectCoWorkers`
// is read downstream as `!== undefined`, matching legacy `buildFilter`'s
// check on the raw querystring value, so an absent param must not add a
// `primary_company_id = false` clause that matches nothing.
const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => (v === undefined ? undefined : v === true || v === 'true'));

export const UsersListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  q: z.string().max(255).optional(),
  filter: z.string().max(512).optional(),
  companyId: z.string().optional(),
  role: z.string().optional(),
  selectCoWorkers: booleanLike,
  isDropDown: booleanLike,
});

export type UsersListQuery = z.infer<typeof UsersListQuerySchema>;

/**
 * Row shape returned by the scoped users list, in both the paginated and
 * `isDropDown` modes. `looseObject` because the paginated SELECT returns
 * ~15 columns while the dropdown SELECT returns only 6 — only `id` is
 * guaranteed present in both.
 *
 * Field reads in the FE (must stay populated in the mode that supplies them):
 *   id, firstName, lastName, email, companyId, companyName,
 *   avatarFileUrl, updatedAt, updatedByUserName, updatedByCompanyName,
 *   hasAgreedWithTerms, hasAgreedWithTermsAt, lastLoginAt
 *
 * `email` (and a handful of other sensitive fields, including
 * `hasAgreedWithTerms`, `hasAgreedWithTermsAt` and `lastLoginAt` — see
 * `core/db/users/list-scoped.ts`) is omitted by the SQL itself, not just
 * hidden here, when the caller is browsing another company's users without
 * the `view-network-users-info` platform feature enabled.
 *
 * There is no display `role`/`roleName` column any more — `role` still
 * exists as a `filter`/`sort` query capability only (see `FILTER_COLUMNS`/
 * `SORT_COLUMNS` in `core/db/users/list-scoped-filter.ts`).
 */
export const UserListRowSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  companyId: z.string().nullable().optional(),
  /**
   * The IAM permission sets this user holds in the row's own company, by key.
   * Absent in `isDropDown` mode, which selects a narrower set of columns.
   */
  roleKeys: z.array(z.string()).optional(),
});

export type UserListRow = z.infer<typeof UserListRowSchema>;

export const UsersListResponseSchema = z.object({
  data: z.array(UserListRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type UsersListResponse = z.infer<typeof UsersListResponseSchema>;
